/**
 * Cliente de la cola agregada de validaciones de la Comisión (Sprint 19 A2).
 *
 * Un solo request a `GET /admin/validaciones/pendientes` trae todo lo
 * pendiente del barrio (residentes + vehículos + citofonía + wifi),
 * reemplazando el patrón viejo de iterar los ~71 lotes con N requests.
 */
import { apiFetch } from './client';

/** Campos de estado/auditoría compartidos (S21 historial): permiten que la
 *  misma estructura sirva para las pestañas Validadas/No validadas/Todas. */
export interface AuditoriaValidacion {
  estado: string;
  validado_at: string | null;
  motivo_rechazo: string | null;
  validado_por_nombre: string | null;
}

export interface ResidentePendiente extends AuditoriaValidacion {
  id: string;
  lote_id: string;
  lote_identificador: string;
  persona_nombre_completo: string;
  // Datos verificables por la Comisión (S19-A4).
  persona_rut: string | null;
  persona_email: string | null;
  persona_celular: string | null;
  parentesco: string;
  desde: string;
  motivo_pendiente: string | null;
  creado_at: string;
}

export interface VehiculoPendiente extends AuditoriaValidacion {
  id: string;
  lote_id: string;
  lote_identificador: string;
  patente: string;
  marca: string | null;
  modelo: string | null;
  color: string | null;
  tipo_traccion: string;
  motivo_pendiente: string | null;
  creado_at: string;
}

export interface AccesoPendiente extends AuditoriaValidacion {
  id: string;
  tipo: 'citofonia' | 'wifi';
  lote_id: string;
  lote_identificador: string;
  residente_id: string;
  residente_nombre: string;
  // Ficha del residente (S19-A4): cruce de datos verificables.
  residente_rut: string | null;
  residente_email: string | null;
  residente_celular: string | null;
  slot: number | null;
  // Dato que la Comisión valida en el acceso: citofonía → celular que abre
  // el portón; wifi → email de credenciales.
  celular_snapshot: string | null;
  email_snapshot: string | null;
  motivo_pendiente: string | null;
  creado_at: string;
}

/**
 * Designación de autorizado de portal esperando a la Comisión.
 *
 * Trae la `justificacion` porque es el dato por el que existe este flujo: la
 * Comisión decide leyendo el motivo que escribió el titular, no adivinando.
 */
export interface DesignacionAutorizadoPendiente {
  id: string;
  lote_id: string;
  lote_identificador: string;
  residente_id: string;
  residente_nombre: string;
  residente_parentesco: string;
  residente_email: string | null;
  titular_nombre: string;
  justificacion: string;
  estado: string;
  solicitado_at: string;
  revisado_at: string | null;
  motivo_rechazo: string | null;
  revisado_por_nombre: string | null;
}

export interface ValidacionesPendientesResponse {
  residentes: ResidentePendiente[];
  vehiculos: VehiculoPendiente[];
  citofonia: AccesoPendiente[];
  wifi: AccesoPendiente[];
  autorizados: DesignacionAutorizadoPendiente[];
}

/** La Comisión aprueba: recién aquí se provisiona la cuenta del autorizado. */
export function aprobarDesignacionAutorizado(
  designacionId: string,
): Promise<DesignacionAutorizadoPendiente> {
  return apiFetch<DesignacionAutorizadoPendiente>(
    `/residentes/designaciones-autorizado/${designacionId}/aprobar`,
    { method: 'POST' },
  );
}

/** El motivo es obligatorio: el titular lo va a ver. */
export function rechazarDesignacionAutorizado(
  designacionId: string,
  motivo: string,
): Promise<DesignacionAutorizadoPendiente> {
  return apiFetch<DesignacionAutorizadoPendiente>(
    `/residentes/designaciones-autorizado/${designacionId}/rechazar`,
    { method: 'POST', body: JSON.stringify({ motivo }) },
  );
}

/** Filtro de las pestañas de la página Validaciones (S21). */
export type FiltroEstadoValidacion =
  | 'pendiente'
  | 'validadas'
  | 'rechazadas'
  | 'todas';

export function listarPendientes(): Promise<ValidacionesPendientesResponse> {
  return apiFetch<ValidacionesPendientesResponse>(
    '/admin/validaciones/pendientes',
  );
}

/** Validables del barrio filtrados por estado (pestañas de la página). */
export function listarValidables(
  estado: FiltroEstadoValidacion,
): Promise<ValidacionesPendientesResponse> {
  return apiFetch<ValidacionesPendientesResponse>(
    `/admin/validaciones?estado=${estado}`,
  );
}
